import { useState, useEffect, createContext, useContext } from 'react';
import { api } from '../lib/axios';
import { useAuth } from './AuthContext';

interface FeatureAccessContextType {
    hasFeature: (featureKey: string) => boolean;
    subscription: any;
    loading: boolean;
}

const FeatureAccessContext = createContext<FeatureAccessContextType>({
    hasFeature: () => false,
    subscription: null,
    loading: true
});

export const FeatureAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [subscription, setSubscription] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.role === 'OWNER') {
            fetchSubscription();
        } else {
            setLoading(false);
        }
    }, [user]);

    const fetchSubscription = async () => {
        try {
            const res = await api.get('/subscription/current');
            setSubscription(res.data);
        } catch (error) {
            console.error('Failed to fetch subscription status', error);
        } finally {
            setLoading(false);
        }
    };

    const hasFeature = (featureKey: string): boolean => {
        if (!subscription) return false;

        // Admin has access to everything
        if (user?.role === 'ADMIN') return true;

        const features = subscription.tier?.features || [];
        return features.some((f: any) => f.feature.key === featureKey);
    };

    return (
        <FeatureAccessContext.Provider value={{ hasFeature, subscription, loading }}>
            {children}
        </FeatureAccessContext.Provider>
    );
};

export const useFeatureAccess = () => useContext(FeatureAccessContext);
